import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/Drawer'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Table from '@groovehq/internal-design-system/lib/components/Table/Table'

import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { styles as modalBtnsStyles } from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns.styles'
import ProductsSearch from 'components/integrations/ShopifyV2WidgetCard/ProductsSearch'

import CustomItem from 'components/integrations/ShopifyV2WidgetCard/CustomItem'
import OrderSummary from 'components/integrations/ShopifyV2WidgetCard/OrderSummary'
import { useForm } from 'react-hook-form'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import {
  DRAWER_TYPE_SHOPIFY_SEND_INVOICE,
  DRAWER_TYPE_SHOPIFY_UNSAVED_CHANGES,
} from 'ducks/drawers/types'
import { selectCurrentTicketId } from 'selectors/tickets'

import { selectIntegrationCredentialsForId } from 'ducks/integrations'

import {
  selectDraftOrderForCustomerId,
  doDraftOrderComplete,
  doDraftOrderDelete,
  doDraftOrderCalculate,
  selectCurrencyCode,
} from 'ducks/integrations/shopify'
import generateDuplicateOrder from 'ducks/integrations/shopify/generateDuplicateOrder'

import useColumns from './useColumns'
import { styles } from './styles'

const orderIsValid = ({ orderItems }) => {
  if (!orderItems) return false
  const hasEnoughItems = orderItems.length > 0

  return hasEnoughItems
}
const invoice = {}

const tableCss = [styles.table, styles.empty]

const convertOrderItemToLineItem = orderItem => {
  const { price } = orderItem
  let item
  if (orderItem.custom) {
    item = {
      title: orderItem.displayName,
      quantity: +orderItem.quantity,
      originalUnitPrice: +orderItem.customPrice,
      taxable: orderItem.taxable,
      requiresShipping: orderItem.requiresShipping,
    }
  } else {
    item = {
      quantity: +orderItem.quantity,
      variantId: orderItem.id,
    }
  }
  if (price?.discount) {
    const { number, isPercent } = price.discount
    item.appliedDiscount = {
      valueType: isPercent ? 'PERCENTAGE' : 'FIXED_AMOUNT',
      value: number * 1,
    }
  }
  return item
}

const convertOrderItemsToLineItems = orderItems => {
  return orderItems.map(convertOrderItemToLineItem)
}

const ProductList = React.memo(
  ({ columns, orderItems, handleRemoveItem, handleUpdateItem }) => {
    return (
      <div css={styles.tableOverflowOverride}>
        <Table
          columns={columns}
          data={orderItems}
          loading={false}
          css={tableCss}
          deleteData={handleRemoveItem}
          updateData={handleUpdateItem}
          className="grui mt-5"
        />
      </div>
    )
  }
)

const emptyObj = {}

const generateDefaultValues = (order, priceCurrency) => {
  const newOrderDefaults = {
    orderItems: [],
    hasTaxes: false,
    shipping: {},
    discount: false,
    notes: '',
    tags: '',
  }
  if (!order) return newOrderDefaults

  return generateDuplicateOrder(order, priceCurrency)
}

const CreateOrderDrawer = ({
  onClose,
  isDuplicate,
  visible = true,
  drawerResourceId: customerGid,
  drawerIntegrationId,
  fetchDraftOrders,
  draftOrderCreate,
  draftOrderUpdate,
  order,
}) => {
  const currencyCode = useSelector(selectCurrencyCode)
  const [isSending, setSending] = useState(false)
  const [calculatedDraft, setCalculatedDraft] = useState(null)
  const dispatch = useDispatch()
  const defaultValues = useMemo(
    () => {
      return generateDefaultValues(order, currencyCode)
    },
    [order, currencyCode]
  )
  const conversationId = useSelector(selectCurrentTicketId)

  const draftOrder =
    useSelector(state =>
      selectDraftOrderForCustomerId(state, {
        customerId: customerGid,
      })
    ) || emptyObj

  const integration = useSelector(state => {
    return selectIntegrationCredentialsForId(state, {
      id: drawerIntegrationId,
    })
  })

  const {
    drawerId: sendInvoiceDrawerId,
    openDrawer: openSendInvoiceDrawer,
  } = useDrawer({
    type: DRAWER_TYPE_SHOPIFY_SEND_INVOICE,
  })

  const { openDrawer: openUnsavedDrawer } = useDrawer({
    type: DRAWER_TYPE_SHOPIFY_UNSAVED_CHANGES,
  })

  const { register, reset, watch, getValues, setValue, control } = useForm({
    mode: 'all',
    defaultValues,
  })

  const onSendInvoice = useCallback(
    () => {
      if (draftOrder)
        openSendInvoiceDrawer(draftOrder.id, {
          query: {
            ...buildDrawerQueryParam(
              sendInvoiceDrawerId,
              'drawerIntegrationId',
              integration.id
            ),
          },
          additionalProps: {
            customerEmail: draftOrder?.customer?.email,
          },
        })
    },
    [openSendInvoiceDrawer, sendInvoiceDrawerId, draftOrder, integration]
  )

  const runCalculation = useCallback(
    async () => {
      const items = getValues('orderItems')
      const result = await dispatch(
        doDraftOrderCalculate(integration.id, customerGid, {
          lineItems: convertOrderItemsToLineItems(items),
        })
      )
      setCalculatedDraft(result)
    },
    [integration, customerGid, dispatch, getValues, setCalculatedDraft]
  )

  useEffect(
    () => {
      // When dealing with duplicates, we want to create the draft order as soon as possible
      if (isDuplicate && order) {
        const val = defaultValues
        const discount = val.discount
        let appliedDiscount = null
        if (discount) {
          appliedDiscount = {
            valueType: 'FIXED_AMOUNT',
            value: discount.number * 1,
          }
        }
        const payload = {
          appliedDiscount,
          note: val.notes,
          tags: val.tags && val.tags.split(','),
          taxExempt: !val.hasTaxes,
          lineItems: convertOrderItemsToLineItems(val.orderItems),
        }
        draftOrderCreate(integration, customerGid, payload)
      }
    },
    [
      order,
      isDuplicate,
      integration,
      customerGid,
      defaultValues,
      draftOrderCreate,
    ]
  )

  useEffect(
    () => {
      if (integration) {
        fetchDraftOrders(integration, customerGid)
      }
    },
    [integration, fetchDraftOrders, customerGid]
  )

  useEffect(
    () => {
      if (!draftOrder.id) return
      let discount = getValues('discount')
      if (draftOrder.appliedDiscount) {
        discount = {
          number: draftOrder.appliedDiscount.value,
          isPercent: draftOrder.appliedDiscount.valueType === 'PERCENTAGE',
        }
      }
      let shipping = getValues('shipping')
      if (draftOrder.shippingLine) {
        const {
          shippingRateHandle,
          custom,
          originalPriceSet,
          title,
        } = draftOrder.shippingLine
        let type = shippingRateHandle
        if (custom) type = 'custom'
        shipping = {
          type,
          title,
          fee: originalPriceSet?.shopMoney.amount * 1,
        }
      }
      const { tags } = draftOrder
      const resetPayload = {
        ...getValues(),
        notes: draftOrder.note2,
        tags: tags ? tags.join(', ') : '',
        discount,
        shipping,
        orderItems: draftOrder.lineItems,
        hasTaxes: !draftOrder.taxExempt,
      }
      reset(resetPayload)
      if (!calculatedDraft) runCalculation()
    },
    [draftOrder, getValues, reset, runCalculation, calculatedDraft]
  )

  // order fields
  const shipping = watch('shipping')
  const setShipping = useCallback(
    newShipping => {
      let shippingLine = {}
      const { type, fee, title } = newShipping
      if (type === 'free') {
        shippingLine = { price: 0, title: 'Free' }
      } else if (type === 'custom') {
        shippingLine = { price: fee, title: title || 'Custom' }
      } else {
        shippingLine = {
          shippingRateHandle: type,
        }
      }
      setValue('shipping', newShipping)

      if (integration) {
        draftOrderUpdate(integration, draftOrder.id, {
          shippingLine,
          useCustomerDefaultAddress: true,
        })
      }
    },
    [setValue, draftOrder, draftOrderUpdate, integration]
  )

  const discount = watch('discount')
  const setDiscount = useCallback(
    newDiscount => {
      let appliedDiscount
      setValue('discount', newDiscount)
      if (newDiscount) {
        const { number, isPercent } = newDiscount
        appliedDiscount = {
          valueType: isPercent ? 'PERCENTAGE' : 'FIXED_AMOUNT',
          value: number * 1,
        }
      } else {
        appliedDiscount = newDiscount
      }
      if (integration) {
        draftOrderUpdate(integration, draftOrder.id, { appliedDiscount })
      }
    },
    [setValue, draftOrderUpdate, integration, draftOrder.id]
  )

  const hasTaxes = watch('hasTaxes')
  const setHasTaxes = useCallback(
    newValue => {
      const taxExempt = !newValue
      draftOrderUpdate(integration, draftOrder.id, { taxExempt })
      setValue('hasTaxes', newValue)
    },
    [setValue, integration, draftOrder.id, draftOrderUpdate]
  )

  const orderItems = watch('orderItems')
  const setOrderItems = useCallback(
    newValue => {
      setValue('orderItems', newValue)
    },
    [setValue]
  )

  const setNote = useCallback(
    newValue => {
      draftOrderUpdate(integration, draftOrder.id, { note: newValue })
      setValue('notes', newValue)
    },
    [setValue, integration, draftOrder.id, draftOrderUpdate]
  )

  const setTags = useCallback(
    newValue => {
      draftOrderUpdate(integration, draftOrder.id, {
        tags: newValue.split(','),
      })
      setValue('tags', newValue)
    },
    [setValue, integration, draftOrder.id, draftOrderUpdate]
  )

  // callbacks
  const handleCreatePaidOrder = useCallback(
    async e => {
      e.preventDefault()
      setSending(true)
      await dispatch(
        doDraftOrderComplete(
          integration.id,
          draftOrder.id,
          false,
          conversationId,
          isDuplicate
        )
      )
      setSending(false)
      onClose()
    },
    [
      dispatch,
      integration,
      draftOrder,
      onClose,
      setSending,
      conversationId,
      isDuplicate,
    ]
  )

  const handleCreatePendingOrder = useCallback(
    async e => {
      e.preventDefault()
      setSending(true)
      await dispatch(
        doDraftOrderComplete(
          integration.id,
          draftOrder.id,
          true,
          conversationId,
          isDuplicate
        )
      )
      setSending(false)
      onClose()
    },
    [
      dispatch,
      integration,
      draftOrder,
      onClose,
      setSending,
      conversationId,
      isDuplicate,
    ]
  )

  const handleClose = useCallback(
    () => {
      if (draftOrder && draftOrder.id) {
        openUnsavedDrawer(customerGid, {
          additionalProps: {
            onDiscard: () => {
              dispatch(doDraftOrderDelete(integration.id, draftOrder.id))
            },
          },
        })
      } else {
        onClose()
      }
    },
    [openUnsavedDrawer, onClose, customerGid, draftOrder, dispatch, integration]
  )

  const footer = useMemo(
    () => {
      const isValid = orderIsValid({ orderItems })
      return (
        <div css={[modalBtnsStyles.base, styles.footer]}>
          <Button
            type="primary"
            onClick={handleCreatePaidOrder}
            disabled={isSending || !isValid}
          >
            {isSending && 'Creating order...'}
            {!isSending && 'Create order as paid'}
          </Button>
          <Button
            type="primary"
            onClick={handleCreatePendingOrder}
            disabled={isSending || !isValid}
          >
            {isSending && 'Creating order...'}
            {!isSending && 'Create order as pending'}
          </Button>
          <Button type="tertiary" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      )
    },
    [
      handleClose,
      orderItems,
      handleCreatePaidOrder,
      handleCreatePendingOrder,
      isSending,
    ]
  )

  const handleAddItem = useCallback(
    variant => {
      const hasItemAlready =
        orderItems.filter(item => item.id === variant.id).length > 0
      if (hasItemAlready) return
      const newItem = {
        ...variant,
        variantId: variant.id,
        product: {
          name: variant.displayName,
          stock: variant.inventoryQuantity,
          sku: variant.sku,
        },
        price: {
          price: variant.price * 100, // all prices we get are with decimals
          currency: currencyCode,
        },
        quantity: variant.quantity || 1,
        total: variant.price * 100,
      }
      if (variant.custom) {
        newItem.customPrice = variant.price
      }
      const newItems = [...orderItems, newItem]
      setOrderItems(newItems)
      if (!draftOrder.id) {
        if (variant.custom) {
          draftOrderCreate(integration, customerGid, {
            lineItems: convertOrderItemsToLineItems(newItems),
          }).then(runCalculation)
        } else {
          draftOrderCreate(
            integration,
            customerGid,
            {
              lineItems: convertOrderItemsToLineItems([
                { quantity: 1, id: variant.id },
              ]),
            },
            runCalculation
          )
        }
      } else {
        draftOrderUpdate(
          integration,
          draftOrder.id,
          {
            lineItems: convertOrderItemsToLineItems(newItems),
          },
          runCalculation
        )
      }
    },
    [
      draftOrderCreate,
      draftOrderUpdate,
      draftOrder,
      orderItems,
      setOrderItems,
      integration,
      customerGid,
      runCalculation,
      currencyCode,
    ]
  )

  const handleRemoveItem = useCallback(
    id => {
      const newItems = orderItems.filter(item => item.id !== id)
      setOrderItems(newItems)
      draftOrderUpdate(integration, draftOrder.id, {
        lineItems: convertOrderItemsToLineItems(newItems),
      })
    },
    [draftOrderUpdate, draftOrder, orderItems, setOrderItems, integration]
  )

  const handleUpdateItem = useCallback(
    (index, id, value) => {
      const items = getValues('orderItems')
      let newItem
      const item = items[index]
      if (id === 'quantity') {
        newItem = { ...item, quantity: value }
      }
      if (id === 'total') {
        newItem = { ...item, total: value }
      }
      if (id === 'bulk') {
        newItem = { ...item, ...value }
      }
      if (newItem) {
        const newItems = [...items]
        newItems.splice(index, 1, newItem)
        setOrderItems(newItems)
        draftOrderUpdate(
          integration,
          draftOrder.id,
          {
            lineItems: convertOrderItemsToLineItems(newItems),
          },
          runCalculation
        )
      }
    },
    [
      getValues,
      setOrderItems,
      integration,
      draftOrder.id,
      draftOrderUpdate,
      runCalculation,
    ]
  )

  const handleUpdateDraft = useCallback(
    payload => {
      draftOrderUpdate(integration, draftOrder.id, payload)
    },
    [integration, draftOrder.id, draftOrderUpdate]
  )

  const columns = useColumns()

  const orderTotal = useMemo(
    () => {
      let total = 0
      if (orderItems)
        orderItems.forEach(item => (total += parseInt(item.total, 10)))
      return total
    },
    [orderItems]
  )

  return (
    <Drawer
      title={`${isDuplicate ? 'Duplicate' : 'Create'} order`}
      footer={footer}
      onClose={handleClose}
      open={visible}
      css={text.styles.textDark}
      size="wide"
    >
      <div className="grui flex mb-8">
        <ProductsSearch
          className="grui flex-1"
          onAddItem={handleAddItem}
          integration={integration}
          orderItems={orderItems}
        />
        <CustomItem currency={currencyCode} onAddItem={handleAddItem} />
      </div>
      <ProductList
        columns={columns}
        orderItems={orderItems}
        handleRemoveItem={handleRemoveItem}
        handleUpdateItem={handleUpdateItem}
      />
      <OrderSummary
        orderItemsCount={orderItems.length}
        currency={currencyCode}
        rawTotal={orderTotal}
        onCloseCreateOrder={onClose}
        onSendInvoice={onSendInvoice}
        canSendInvoice={!!draftOrder.id}
        invoice={invoice}
        hasTaxes={hasTaxes}
        setHasTaxes={setHasTaxes}
        discount={discount}
        setDiscount={setDiscount}
        register={register}
        shipping={shipping}
        setShipping={setShipping}
        calculatedDraft={calculatedDraft}
        totalTax={draftOrder.totalTax}
        totalPrice={draftOrder.totalPrice}
        subtotalPrice={draftOrder.subtotalPrice}
        setNote={setNote}
        setTags={setTags}
        invoiceSentAt={draftOrder.invoiceSentAt}
        className="grui mt-10"
        control={control}
        updateDraft={handleUpdateDraft}
      />
    </Drawer>
  )
}

export default CreateOrderDrawer
